import seoRoutePaths from "./seo-route-paths.json" with { type: "json" };

export const SITE_ORIGIN = "https://permitpilott.com";
export const SITE_NAME = "PermitPilott";
export const DEFAULT_IMAGE = null;

const defaultDescription =
  "Turn oversize/overweight permit PDFs or photos into editable routes. Review your route, navigate in-app, or export GPX. Start with 3 free routes.";

export const defaultSeo = {
  title: "Oversize/Overweight Permit Routing | PermitPilott",
  description: defaultDescription,
  canonicalPath: "/",
  robots: "index,follow",
  type: "website",
};

const webApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "PermitPilott",
  url: SITE_ORIGIN,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: defaultDescription,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description:
      "Start with 3 free routes. Texas-Lite covers Texas permits for $10/month; Pro includes all 50 states and Route Planner.",
  },
};

const pricingDescription =
  "Compare oversize/overweight permit routing plans: 3 free routes, Texas-Lite for Texas permits at $10/month, or Pro for all 50 states and Route Planner.";

const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "PermitPilott",
  description: pricingDescription,
  brand: {
    "@type": "Brand",
    name: "PermitPilott",
  },
  offers: [
    {
      "@type": "Offer",
      name: "Free Trial",
      price: "0",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Texas-Lite Monthly",
      description:
        "Unlimited Texas permit routes for $10 USD per month. Monthly billing only; Route Planner is not included.",
      price: "10.00",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Pro Monthly",
      price: "29.99",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Pro Yearly",
      price: "289.99",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
  ],
};

export const seoRoutes = {
  [seoRoutePaths.home]: {
    ...defaultSeo,
    structuredData: webApplicationSchema,
  },
  [seoRoutePaths.pricing]: {
    title: "OS/OW Permit Routing Pricing | PermitPilott",
    description: pricingDescription,
    canonicalPath: "/pricing",
    robots: "index,follow",
    type: "website",
    structuredData: pricingSchema,
  },
  [seoRoutePaths.terms]: {
    title: "Terms of Service - PermitPilott",
    description:
      "Read the PermitPilott terms of service for using oversized load permit reading, PDF-to-route conversion, route planning, navigation, export, and account features.",
    canonicalPath: "/terms",
    robots: "index,follow",
    type: "article",
  },
  [seoRoutePaths.privacy]: {
    title: "Privacy Policy - PermitPilott",
    description:
      "Learn how PermitPilott handles account, permit, routing, billing, and usage information.",
    canonicalPath: "/privacy",
    robots: "index,follow",
    type: "article",
  },
  [seoRoutePaths.disclaimer]: {
    title: "Routing-Aid Disclaimer - PermitPilott",
    description:
      "PermitPilott is a routing aid for oversized permit directions, not a routing authority. Always verify routes against the issued permit.",
    canonicalPath: "/disclaimer",
    robots: "index,follow",
    type: "article",
  },
};

const noindexRouteTitles = {
  "/s": `Shared Route - ${SITE_NAME}`,
  "/login": `Sign in - ${SITE_NAME}`,
  "/signup": `Create Account - ${SITE_NAME}`,
  "/forgot-password": `Forgot Password - ${SITE_NAME}`,
  "/reset-password": `Reset Password - ${SITE_NAME}`,
  "/verify": `Verify Email - ${SITE_NAME}`,
  "/dashboard": `Home - ${SITE_NAME}`,
  "/routes/new": `New route - ${SITE_NAME}`,
  "/routes/:routeId": `Saved route - ${SITE_NAME}`,
  "/account": `Account - ${SITE_NAME}`,
  "/organizations/:organizationId/settings": `Organization Settings - ${SITE_NAME}`,
  "/routes": `Routes - ${SITE_NAME}`,
  "/planner": `Route Planner - ${SITE_NAME}`,
  "/admin": `Administration - ${SITE_NAME}`,
  "/admin/shared-route/:adminShareId": `Shared Route Admin Preview - ${SITE_NAME}`,
};

export const noindexRoutes = new Set(Object.keys(noindexRouteTitles));

export const publicSeoPaths = Object.values(seoRoutePaths);

function normalizePath(path) {
  const cleanPath = (path || "/").split("?")[0].split("#")[0];
  if (cleanPath.length > 1 && cleanPath.endsWith("/"))
    return cleanPath.slice(0, -1);
  return cleanPath || "/";
}

export function getSeoForPath(path) {
  const normalizedPath = normalizePath(path);
  const titlePath = normalizedPath
    .replace(/^\/routes\/(?!new$)[^/]+$/, "/routes/:routeId")
    .replace(
      /^\/organizations\/[^/]+\/settings$/,
      "/organizations/:organizationId/settings",
    )
    .replace(
      /^\/admin\/shared-route\/[^/]+$/,
      "/admin/shared-route/:adminShareId",
    );
  if (seoRoutes[normalizedPath]) return seoRoutes[normalizedPath];
  if (noindexRoutes.has(titlePath)) {
    return {
      title: noindexRouteTitles[titlePath],
      description: defaultDescription,
      canonicalPath: normalizedPath,
      robots: "noindex,nofollow",
      type: "website",
    };
  }
  return {
    ...defaultSeo,
    canonicalPath: normalizedPath,
    robots: "noindex,nofollow",
  };
}

export function canonicalUrl(path) {
  const normalizedPath = normalizePath(path);
  return `${SITE_ORIGIN}${normalizedPath === "/" ? "" : normalizedPath}`;
}

function upsertMetaByName(name, content) {
  upsertMeta("name", name, content);
}

function upsertMetaByProperty(property, content) {
  upsertMeta("property", property, content);
}

function upsertMeta(attribute, value, content) {
  if (!content) return;
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertStructuredData(data) {
  const id = "structured-data";
  let element = document.getElementById(id);
  if (!data) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

export function applyRouteSeo(route, image = DEFAULT_IMAGE) {
  if (typeof document === "undefined") return;

  const seo = getSeoForPath(route.path);
  const url = canonicalUrl(seo.canonicalPath);

  document.title = seo.title;
  upsertMetaByName("description", seo.description);
  upsertMetaByName("robots", seo.robots);
  upsertLink("canonical", url);

  upsertMetaByProperty("og:site_name", SITE_NAME);
  upsertMetaByProperty("og:title", seo.title);
  upsertMetaByProperty("og:description", seo.description);
  upsertMetaByProperty("og:type", seo.type || "website");
  upsertMetaByProperty("og:url", url);

  upsertMetaByName("twitter:card", image ? "summary_large_image" : "summary");
  upsertMetaByName("twitter:title", seo.title);
  upsertMetaByName("twitter:description", seo.description);

  if (image) {
    upsertMetaByProperty("og:image", image);
    upsertMetaByName("twitter:image", image);
  }

  upsertStructuredData(seo.structuredData);
}
